import formAddressFieldsMixin from '@maison/mixins/formAddressFieldsMixin';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import * as formHelper from '@phoenix/helpers/form-helper';
import * as types from '@phoenix/store/mutations-types';
import { difference, filter, find, forEach, get, isEmpty, isObject, set, uniqueId, unset } from 'lodash-es';
import { mapActions, mapMutations, mapState } from 'vuex';

export default {
  mixins: [formAddressFieldsMixin],

  props: {
    groups: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
    phoneRequired: {
      type: Boolean,
      default: false,
    },
    isLightCheckout: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      countryGroups: {},
      formConfigName: 'contact',
      addressFields: {},
      valuesBeforeChangingCountry: {},
      prefix: '',
      currentLanguage: window.Phoenix.laravelLanguage,
      isAddressFieldsLoading: false,
    };
  },

  computed: {
    ...mapState('form', ['dynamicFields']),

    ...mapState('currentUser', ['customerInfo', 'isLogged']),

    isCountryUs() {
      return this.fields.country.value === 'US';
    },

    isCountryHK() {
      return this.fields.country.value === 'HK';
    },

    isCountryAU() {
      return this.fields.country.value === 'AU';
    },

    isCountryKR() {
      return this.fields.country.value === 'KR';
    },

    isCountryUAE() {
      return this.fields.country.value === 'AE';
    },

    isCountryJP() {
      return this.fields.country.value === 'JP';
    },

    isCountryKSA() {
      return this.fields.country.value === 'SA';
    },

    isCountry() {
      return (countries) => countries.includes(this.fields.country.value);
    },

    fieldIsPresent() {
      return (fieldName) => {
        let present = false;
        forEach(this.addressFields, (address) => {
          if (address.name === fieldName) {
            present = true;
            return false;
          }
        });
        return present;
      };
    },

    isArabicLanguage() {
      return this.currentLanguage === 'ar';
    },

    isChineseInPDG() {
      return this.fields?.country?.value === 'CN' && window.Phoenix.currentCountry === 'CN';
    },

    shouldDisplaySpecificHKTcFields() {
      return this.isCountryHK && ['zh-Hans', 'zh-Hant'].includes(this.currentLanguage);
    },

    shouldDisplaySpecificHKEnFields() {
      return this.isCountryHK && this.currentLanguage === 'en';
    },

    shouldDisplaySpecificChineseFields() {
      return (
        this.fieldIsPresent('address7') &&
        this.fieldIsPresent('address8') &&
        this.fieldIsPresent('address9') &&
        this.isChineseInPDG
      );
    },

    selectedCountry() {
      return this.fields?.country?.value || '';
    },

    selectedCountryGroup() {
      const group =
        find(Object.keys(this.countryGroups), (group) =>
          this.countryGroups[group].countries.includes(this.selectedCountry),
        ) || 'SAP';

      return this.countryGroups[group];
    },

    formConfig() {
      const formConfigName =
        this.isChineseInPDG && ['address', 'catalogOrder'].includes(this.formConfigName)
          ? `${this.formConfigName}PDGFormFields`
          : `${this.formConfigName}FormFields`;

      return get(this.selectedCountryGroup, ['formsConfig', formConfigName], []);
    },

    displayFormat() {
      if (!this.selectedCountryGroup) {
        return null;
      }

      const displayFormats = this.selectedCountryGroup.displayFormats;

      if (!displayFormats) {
        return null;
      }

      if (!displayFormats.exceptions) {
        return displayFormats.default;
      }

      const exceptions = displayFormats.exceptions || [];
      const exception = exceptions.find((exception) => exception.countries.includes(this.selectedCountry));

      return exception ? exception.format : displayFormats.default;
    },

    streetFields() {
      if (
        !this.isLightCheckout &&
        this.isCountryKR &&
        !getFeatureFlag(FEATURE_FLAG.GEMINI_KR) &&
        getFeatureFlag(FEATURE_FLAG.KOREAN_POST)
      ) {
        return this.setCorrectOrderFields('zip', 'address5');
      }

      if (this.isCountryUAE) {
        return filter(['address2', 'address5'], (field) => this.fieldIsPresent(field));
      }

      return filter(this.setCorrectOrderFields('address2', 'address5'), (field) => this.fieldIsPresent(field));
    },

    shouldDisplayCityFields() {
      if (this.isCountryKR) {
        return this.fieldIsPresent('address7') && !this.shouldDisplaySpecificChineseFields;
      }

      return this.cityFields.length > 0 && !this.shouldDisplaySpecificChineseFields;
    },

    cityFields() {
      if (this.isCountryKR) {
        return this.setCorrectOrderFields('zip', 'address7');
      }

      if (this.isCountryKSA || this.isCountryUAE) {
        return [];
      }

      return filter(this.setCorrectOrderFields('zip', 'address7'), (field) => this.fieldIsPresent(field));
    },

    shouldDisplayForm() {
      return !this.isAddressFieldsLoading;
    },
  },

  created() {
    this.prefix = uniqueId('id_');
  },

  beforeMount() {
    this.setCountryOptions(this.countries);
  },

  mounted() {
    this.countryGroups = this.groups;
    this.setAddressFields();
  },

  methods: {
    ...mapActions('form', ['loadDynamicFields', 'setHasLoadingGooglePlaces']),

    ...mapMutations('form', [types.FORM_SET_HAS_GOOGLE_PLACES_LOADED]),

    getComponent: formHelper.getComponent,

    getPasswordRequirements: formHelper.getPasswordRequirements,

    setAddressFields(valuesInForm = null) {
      const ancientAddressFields = JSON.parse(JSON.stringify(this.addressFields));

      if (!this.selectedCountryGroup) {
        return [];
      }

      // Retrieve only the fields we need for this form
      let fields = JSON.parse(JSON.stringify(this.selectedCountryGroup.fields));

      fields = fields.filter(
        (field) =>
          this.formConfig.includes(field.id) ||
          this.formConfig.find((configField) => isObject(configField) && configField.id === field.id),
      );

      this.addressFields = fields
        .map((field) =>
          this.translateField({
            ...field,
            ...this.formConfig.find((configField) => isObject(configField) && configField.id === field.id),
            ...(this.phoneRequired && ['mobilePhone', 'phone'].includes(field.name) && { required: true }),
          }),
        )
        .filter((field) => {
          const method = get(field, 'conditionsMethod', 'every');
          return get(field, 'conditions', [])[method]((condition) => {
            const method = get(condition, 'method');
            const params = get(condition, 'params', []);
            const result = get(condition, 'result', true);

            return this[method](...params) === result;
          });
        });

      // Save the previous values in the form as we'll maybe need them later if we come back on the same country.
      // Also reset fields props that are not set in every countries
      Object.keys(this.fields).forEach((field) => {
        this.valuesBeforeChangingCountry[field] = this.fields[field].value;

        unset(this.fields[field], 'pattern');
        unset(this.fields[field], 'inputmode');
        unset(this.fields[field], 'validation');
      });

      // Remove in fields the fields we don't have anymore.
      if (!isEmpty(ancientAddressFields)) {
        difference(
          ancientAddressFields.map((address) => address.name),
          this.addressFields.map((address) => address.name),
        ).forEach((name) => {
          unset(this.fields, name);
        });
      }

      if (this.selectedCountry !== '') {
        this.loadDynamicFields(this.selectedCountry);
      }

      this.addressFields.forEach((address) => {
        if (this.valuesBeforeChangingCountry[address.name] !== undefined) {
          address.value = this.valuesBeforeChangingCountry[address.name];
        }

        // DSPHX-1366: We need to remove length when we change country otherwise we can end up with an unwanted validation rule
        if (this.fields[address.name]?.length) {
          unset(this.fields[address.name], 'length');
        }

        set(this.fields, address.name, {
          ...this.fields[address.name],
          ...JSON.parse(JSON.stringify(address)),
        });

        // The input elements should not have the same ID on the DOM.
        // So we have to generate an uniqueID by form
        Object.keys(this.fields).forEach((name) => {
          const field = this.fields[name];
          field.uniqueId = `${this.prefix}_${field.id}`;
          // PIA-6627 As the address, name, phone & emails fields are sensitive, all the fields from the address.json are sensitive
          field.sensitive = true;
          set(this.fields, field.name, field);
        });

        this.setDynamicOptions(this.selectedCountry, address.name, this.dynamicFields);
      });

      if (valuesInForm) {
        formHelper.setInfoInForm(this.fields, valuesInForm);
      }

      formHelper.setInfoInForm(this.fields, this.customerInfo);

      this.afterSetAddressFieldsHook();
    },

    afterSetAddressFieldsHook() {},

    translateField(field) {
      if (!field.translated) {
        field.label = this.$t(`Form.label.${field.label}`);
        if (field.options) {
          field.options.forEach((option) => (option.label = this.$t(`Form.options.${option.label}`)));
        }
        if (field.help !== undefined) {
          field.help = this.$t(field.help);
        }
        field.translated = true;
      }

      return field;
    },

    checkFeatureFlag(featureFlag, language) {
      const isFeatureFlag = getFeatureFlag(featureFlag);
      if (language) {
        return isFeatureFlag && this.checkIsLanguage(language);
      }
      return isFeatureFlag;
    },

    checkIsLogged() {
      return this.isLogged;
    },

    checkIsLanguage(language) {
      if (Array.isArray(language)) {
        return language.includes(this.currentLanguage);
      }
      return this.currentLanguage === language;
    },

    setDynamicOptions(countryCode, name, dynamicFields) {
      if (
        dynamicFields[countryCode] &&
        dynamicFields[countryCode].states &&
        name === 'address9' &&
        this.fieldIsPresent('address9')
      ) {
        if (countryCode === 'AE') {
          set(this, ['fields', 'address9', 'label'], this.$t('Form.label.emirate'));
        }

        if (countryCode === 'SA') {
          set(this, ['fields', 'zip', 'required'], true);
        }

        if (!['', 'CN'].includes(countryCode)) {
          set(this, ['fields', 'address9', 'options'], dynamicFields[countryCode].states);
        }
      }
    },

    setInfoInFormWithCountry(fields, values) {
      const country = values.country;

      if (!country) {
        formHelper.setInfoInForm(fields, values);
        return;
      }
      set(this.fields, ['country', 'value'], country);
      this.setAddressFields(values);
    },

    setCorrectOrderFields(fieldA, fieldB) {
      const fields = [fieldA, fieldB];
      if (!this.displayFormat) {
        return fields;
      }

      const line = this.displayFormat.find((line) => line.includes(fieldA));

      if (!line) {
        return fields;
      }

      return line.split(fieldA)[0].includes(fieldB) ? fields.reverse() : fields;
    },

    setCountryOptions(options) {
      // Shallow copy the props so we can sort it afterward
      const countries = options.slice();
      if (this.fields.country !== undefined) {
        this.fields.country.options = countries.sort((a, b) => a.label.localeCompare(b.label));
      }
    },

    isCountryUsingGooglePlaces(countryCode) {
      return ['AE', 'SA'].includes(countryCode);
    },

    loadGooglePlaces() {
      if (window.google) {
        return;
      }

      const placesScript = document.createElement('script');

      window._initGooglePlace = () => this[types.FORM_SET_HAS_GOOGLE_PLACES_LOADED](true);

      placesScript.setAttribute(
        'src',
        `https://maps.googleapis.com/maps/api/js?key=${window.Phoenix.config.GOOGLE_MAPS_API_KEY}&libraries=places&callback=_initGooglePlace`,
      );
      document.head.appendChild(placesScript);
    },
  },

  watch: {
    'fields.country.value'(value) {
      this.isAddressFieldsLoading = true;
      this.setAddressFields();

      if (
        this.isCountryUsingGooglePlaces(value) &&
        (this.fieldIsPresent('placeId') || this.fieldIsPresent('address11'))
      ) {
        this.loadGooglePlaces();
      }

      this.$nextTick(() => {
        this.isAddressFieldsLoading = false;
      });
    },

    countries: {
      handler(value) {
        this.setCountryOptions(value);
      },
      immediate: true,
    },

    dynamicFields: {
      handler(value) {
        this.setDynamicOptions(this.fields.country.value || '', 'address9', value);
      },
      deep: true,
    },

    customerInfo(value) {
      if (value) {
        this.setAddressFields();
      }
    },
  },
};
